import { useRouteError, Link, useNavigate } from 'react-router-dom';
import { Icon } from '@nike/eds';

const NotFoundError = () => (
  <>
    <p className='tw-text-sm tw-font-medium tw-text-blue-500 dark:tw-text-blue-400'>
      404 error
    </p>
    <h1 className='tw-mt-3 tw-text-2xl tw-font-semibold tw-text-gray-800 dark:tw-text-white md:tw-text-3xl'>
      We can’t find that page
    </h1>
    <p className='tw-mt-4 tw-text-gray-500 dark:tw-text-gray-400'>
      Sorry, the page you are looking for doesn&apos;t exist or has been moved.
    </p>
  </>
);

const GeneralError = ({
  error,
}: {
  error: { statusText: string; message: string };
}) => (
  <>
    <h1 className='tw-mt-3 tw-text-2xl tw-font-semibold tw-text-gray-800 dark:tw-text-white md:tw-text-3xl'>
      Oops!
    </h1>
    <p className='tw-text-sm tw-font-medium tw-text-blue-500 dark:tw-text-blue-400'>
      Sorry, an unexpected error has occurred.
    </p>
    <p className='tw-mt-4 tw-text-gray-500 dark:tw-text-gray-400'>
      <i>{error.statusText || error.message}</i>
    </p>
  </>
);
// use this as root error boundary
export default function ErrorPage() {
  const error = useRouteError() as {
    statusText: string;
    message: string;
    status?: number;
  };
  console.error(error);
  const navigate = useNavigate();

  return (
    <section className='tw-bg-white dark:tw-bg-gray-900 '>
      <div className='tw-container tw-flex tw-items-center tw-min-h-screen tw-px-6 tw-py-12 tw-mx-auto'>
        <div>
          {error.status === 404 ? (
            <NotFoundError />
          ) : (
            <GeneralError error={error} />
          )}
          <div className='tw-flex tw-items-center tw-mt-6 tw-gap-x-3'>
            <button
              className='error-page-back-btn'
              onClick={() => navigate(-1)}
            >
              <Icon name='ArrowLeft' size='s' />
              Go back
            </button>
            <Link to='/' className='error-page-home-btn'>
              Take me home
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const getErrorDisplayMessage = (error: { message: string; status: number }) => {
  if (error.status === 401) {
    return {
      title: 'Unauthorized',
      message: 'Please refresh page or re-login',
    };
  }
  if (error.status === 403) {
    return {
      title: 'Permission Denied',
      message: 'You are not assigned to this scope',
    };
  }
  if (/^4\d{2}$/.test(error.status + '')) {
    return {
      title: 'Bad Input',
      message: error.message || 'Please check your request input',
    };
  }
  if (/^5\d{2}$/.test(error.status + '')) {
    return {
      title: 'Service Error',
      message: error.message || 'Please try again later',
    };
  }
  return {
    title: 'Unexpected error',
    message: error.message || 'Please try again later',
  };
};

// use this as error boundary for child page component;
export const ErrorBoundaryElement = () => {
  const error = useRouteError() as {
    statusText: string;
    message: string;
    status?: number;
  };
  console.error(error);

  if (error.status) {
    const display = getErrorDisplayMessage(
      error as { message: string; status: number },
    );
    return (
      <div className='tw-p-40'>
        <p className='tw-text-sm tw-font-medium tw-text-red-700'>
          {error.status}
        </p>
        <h1 className='tw-mt-3 tw-text-2xl tw-font-semibold tw-text-blue-500 '>
          {display.title}
        </h1>
        <p className='tw-mt-4 tw-text-gray-500 dark:tw-text-gray-400'>
          {display.message}
        </p>
      </div>
    );
  } else {
    return (
      <div className='tw-p-40'>
        <h1 className='tw-mt-3 tw-text-2xl tw-font-semibold tw-text-red-700 '>
          Oops!
        </h1>
        <p className='tw-text-sm tw-font-medium tw-text-blue-500 dark:tw-text-blue-400'>
          Sorry, an unexpected error has occurred.
        </p>
        <p className='tw-mt-4 tw-text-gray-500 dark:tw-text-gray-400'>
          <i>{error.statusText || error.message}</i>
        </p>
      </div>
    );
  }
};
